// Shared drill-in components used by /chickens/:id and /cattle/:id detail
// pages (and could be lifted into a future /segments/:id page).
//
// Three exports, each takes a `scope` prop:
//   { chicken_batch_id: <n> }  for a broiler/layer batch
//   { cattle_id:        <n> }  for an individual animal
//
// - <BatchPLSummary>          income / OpEx / net / per-head numbers, pulled from /api/reports/batch-pl
// - <BatchTransactions>       all income + expense rows tagged to this scope, click-to-edit via TransactionEditor
// - <BatchDistributionPreview> waterfall scoped to this batch/animal, with inline override controls

import { useEffect, useState } from 'react';
import { api, fmtMoney, fmtDate } from '../lib/api.js';
import TransactionEditor from './TransactionEditor.jsx';

// ─── Shared helpers ─────────────────────────────────────────────────────────

function thisYear() { return new Date().getFullYear(); }
function ytdFrom()  { return `${thisYear()}-01-01`; }
function ytdTo()    { return `${thisYear()}-12-31`; }

function scopeToQuery(scope) {
  if (scope?.chicken_batch_id) return `chicken_batch_id=${scope.chicken_batch_id}`;
  if (scope?.cattle_id)        return `cattle_id=${scope.cattle_id}`;
  return '';
}

function Cell({ label, value, tone, bold }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-700/60">{label}</div>
      <div className={`mt-0.5 ${c} ${bold ? 'font-semibold' : ''}`}>{value}</div>
    </div>
  );
}

// ─── 1. P&L summary block ──────────────────────────────────────────────────

export function BatchPLSummary({ scope, segmentId }) {
  const [year, setYear] = useState(thisYear());
  const [row, setRow]   = useState(null);
  const [err, setErr]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setRow(null); setErr('');
    api.get(`/api/reports/batch-pl?year=${year}&all=1`)
      .then(d => {
        if (cancelled) return;
        if (scope?.chicken_batch_id) {
          setRow(d.chicken_batches?.find(b => b.id === scope.chicken_batch_id) || null);
        } else if (scope?.cattle_id) {
          setRow(d.cattle?.find(c => c.id === scope.cattle_id) || null);
        }
      })
      .catch(e => !cancelled && setErr(e.message));
    return () => { cancelled = true; };
  }, [year, scope?.chicken_batch_id, scope?.cattle_id]);

  const isChickens = !!scope?.chicken_batch_id;
  const perUnit = isChickens
    ? (row?.per_head_net_cents ?? 0)
    : (row?.net_cents ?? 0);
  const perUnitLabel = isChickens
    ? '$/bird (net)'
    : '$/head (net)';

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-medium text-olive-900">P&amp;L this {year}</h2>
        <select className="input w-24" value={year} onChange={e => setYear(Number(e.target.value))}>
          {[year - 2, year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {err ? (
        <p className="text-sm text-rust-700">{err}</p>
      ) : !row ? (
        <p className="text-sm text-ink-700/60 italic">No P&amp;L data for {year} yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Cell label="Income"        value={fmtMoney(row.income_cents)}  tone="olive" />
          <Cell label="OpEx"          value={fmtMoney(row.expense_cents)} tone="rust" />
          <Cell label="Net"           value={fmtMoney(row.net_cents)}     tone={row.net_cents >= 0 ? 'olive' : 'rust'} bold />
          <Cell label={perUnitLabel}  value={fmtMoney(perUnit)}            tone={perUnit >= 0 ? 'olive' : 'rust'} />
        </div>
      )}
    </section>
  );
}

// ─── 2. Batch transactions list with in-place edit ─────────────────────────

export function BatchTransactions({ scope, isOwner, refreshKey = 0 }) {
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState('');
  const [editing, setEditing] = useState(null);

  function load() {
    setErr('');
    const qs = scopeToQuery(scope);
    api.get(`/api/transactions?${qs}&limit=200&sort=date&dir=desc`)
      .then(setData)
      .catch(e => setErr(e.message));
  }
  useEffect(() => { load(); }, [scope?.chicken_batch_id, scope?.cattle_id, refreshKey]);

  return (
    <section className="card space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-olive-900">Transactions tagged here</h2>
        <span className="text-xs text-ink-700/60">{data ? `${data.total} rows` : '…'}</span>
      </div>
      {err && <p className="text-sm text-rust-700">{err}</p>}
      {!data ? (
        <p className="text-sm text-ink-700/60 italic">Loading…</p>
      ) : data.transactions.length === 0 ? (
        <p className="text-sm text-ink-700/60 italic">
          Nothing tagged to this {scope?.chicken_batch_id ? 'batch' : 'animal'} yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
                <th className="text-left py-2 px-2">Date</th>
                <th className="text-left py-2 px-2">Type</th>
                <th className="text-left py-2 px-2">Counterparty</th>
                <th className="text-right py-2 px-2">Amount</th>
                <th className="py-2 px-2 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map(t => (
                <tr key={t.key} className="border-b border-olive-100/40 hover:bg-cream-100/40 cursor-pointer"
                    onClick={() => setEditing({ type: t.type, id: t.id })}>
                  <td className="py-1.5 px-2 whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="py-1.5 px-2">
                    <span className={`pill ${t.type === 'income' ? 'bg-olive-100 text-olive-900' : 'bg-rust-500/10 text-rust-700'}`}>
                      {t.type}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 truncate max-w-[200px]">
                    {t.counterparty || <span className="italic text-ink-700/40">—</span>}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-medium whitespace-nowrap ${t.type === 'income' ? 'text-olive-900' : 'text-rust-700'}`}>
                    {t.type === 'income' ? '+' : '−'}{fmtMoney(t.amount_cents)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-ink-700/40">›</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-ink-700/50 mt-2">
            Click any row to edit. Filtered amounts show only the line items matching this {scope?.chicken_batch_id ? 'batch' : 'animal'}.
          </p>
        </div>
      )}
      {editing && (
        <TransactionEditor
          type={editing.type}
          id={editing.id}
          isOwner={isOwner}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

// ─── 3. Batch-scoped distribution preview ──────────────────────────────────

export function BatchDistributionPreview({ scope, segmentId }) {
  const [from, setFrom] = useState(ytdFrom());
  const [to,   setTo]   = useState(ytdTo());
  const [overrides, setOverrides] = useState(null);
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState('');

  function reload(nextOverrides = overrides) {
    setErr('');
    api.post('/api/distributions/preview', {
      segment_id: segmentId,
      from, to,
      scope,
      overrides: nextOverrides,
      include_transactions: false,
    }).then(setData).catch(e => setErr(e.message));
  }
  useEffect(() => { reload(); }, [from, to, segmentId, scope?.chicken_batch_id, scope?.cattle_id]);

  function applyOverrides(patch) {
    const next = { ...(overrides || {}), ...patch };
    setOverrides(next);
    reload(next);
  }
  function resetOverrides() {
    setOverrides(null);
    reload(null);
  }

  if (err) return <section className="card text-sm text-rust-700">{err}</section>;
  if (!data) return <section className="card text-sm text-ink-700/60 italic">Computing distribution…</section>;

  const negative = data.net_profit_cents < 0;

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-medium text-olive-900">Distribution preview ({data.tier === 'T1' ? 'Tier 1' : 'Tier 2'})</h2>
        <div className="flex gap-2 items-end text-xs">
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Cell label="Income"        value={fmtMoney(data.income_cents)}        tone="olive" />
        <Cell label="OpEx"          value={fmtMoney(data.expense_cents)}       tone="rust" />
        <Cell label="Net profit"    value={fmtMoney(data.net_profit_cents)}    tone={negative ? 'rust' : 'olive'} bold />
        <Cell label="Distributable" value={fmtMoney(data.distributable_cents)} tone={data.distributable_cents > 0 ? 'olive' : 'rust'} bold />
      </div>

      {data.distributable_cents > 0 ? (
        <DistributionWaterfall data={data} onOverride={applyOverrides} onReset={resetOverrides} />
      ) : (
        <p className="text-sm text-ink-700/70 italic">
          {negative ? `Net loss of ${fmtMoney(-data.net_profit_cents)} — no distribution this period.`
                    : 'No distributable profit this period.'}
        </p>
      )}
    </section>
  );
}

// Waterfall + inline override controls. Pure presentation; parent owns the
// override state and reload mechanic.
function DistributionWaterfall({ data, onOverride, onReset }) {
  const [showOverrides, setShowOverrides] = useState(false);

  const tier = data.tier;
  const eff  = data.effective_config;
  const def  = data.default_config;
  const opPctKey    = tier === 'T1' ? 'tier1_operator_pct' : 'tier2_operator_pct';
  const jeffPctKey  = tier === 'T1' ? 'tier1_jeff_pct'     : 'tier2_jeff_pct';
  const capexPctKey = tier === 'T1' ? 'tier1_capex_pct'    : null;

  function isOverriddenPct(key) { return def[key] !== eff[key]; }

  return (
    <div className="space-y-2">
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b border-olive-100/40">
            <td className="py-1.5 pl-2">
              Operator pool ({eff[opPctKey]}%)
              {isOverriddenPct(opPctKey) && <span className="ml-2 text-xs text-gold-600">(was {def[opPctKey]}%)</span>}
            </td>
            <td className="py-1.5 px-2 text-right text-olive-900 font-medium">{fmtMoney(data.operator_pool_cents)}</td>
          </tr>
          {data.operator_payouts.map(o => (
            <tr key={o.user_id}>
              <td className="py-1 pl-6 text-xs text-ink-700/70">→ {o.name} ({o.share_pct}%)</td>
              <td className="py-1 px-2 text-right text-xs text-ink-700/80">{fmtMoney(o.amount_cents)}</td>
            </tr>
          ))}
          <tr className="border-b border-olive-100/40">
            <td className="py-1.5 pl-2">
              Jeff ({eff[jeffPctKey]}%)
              {isOverriddenPct(jeffPctKey) && <span className="ml-2 text-xs text-gold-600">(was {def[jeffPctKey]}%)</span>}
            </td>
            <td className="py-1.5 px-2 text-right text-olive-900 font-medium">{fmtMoney(data.jeff_share_cents)}</td>
          </tr>
          {tier === 'T1' && (
            <tr className="border-b border-olive-100/40">
              <td className="py-1.5 pl-2">
                CapEx recovery ({eff[capexPctKey]}%)
                {isOverriddenPct(capexPctKey) && <span className="ml-2 text-xs text-gold-600">(was {def[capexPctKey]}%)</span>}
                <span className="text-xs text-ink-700/60 ml-2">
                  pool: {fmtMoney(data.capex_pool_balance_before)} → {fmtMoney(data.capex_pool_balance_after)}
                </span>
              </td>
              <td className="py-1.5 px-2 text-right text-olive-900 font-medium">{fmtMoney(data.capex_recovery_cents)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center gap-2 text-xs">
        <button onClick={() => setShowOverrides(s => !s)} className="text-olive-700 hover:underline">
          {showOverrides ? 'Hide overrides' : 'Override percentages (preview only)'}
        </button>
        {data.overrides_applied && (
          <button onClick={onReset} className="text-rust-700 hover:underline ml-auto">
            Reset to defaults
          </button>
        )}
      </div>

      {showOverrides && (
        <OverrideEditor data={data} tier={tier} onApply={onOverride} />
      )}
    </div>
  );
}

function OverrideEditor({ data, tier, onApply }) {
  const opPctKey    = tier === 'T1' ? 'tier1_operator_pct' : 'tier2_operator_pct';
  const jeffPctKey  = tier === 'T1' ? 'tier1_jeff_pct'     : 'tier2_jeff_pct';
  const capexPctKey = tier === 'T1' ? 'tier1_capex_pct'    : null;
  const eff = data.effective_config;

  const [opPct,    setOpPct]    = useState(eff[opPctKey]);
  const [jeffPct,  setJeffPct]  = useState(eff[jeffPctKey]);
  const [capexPct, setCapexPct] = useState(capexPctKey ? eff[capexPctKey] : 0);
  const [splits, setSplits]     = useState(data.effective_operators);
  const [warn, setWarn]         = useState('');

  const tierSum  = tier === 'T1' ? (opPct + jeffPct + capexPct) : (opPct + jeffPct);
  const splitSum = splits.reduce((s, o) => s + Number(o.share_pct || 0), 0);

  function applyAll() {
    if (tierSum !== 100) { setWarn(`Tier percentages must sum to 100 (got ${tierSum})`); return; }
    if (splitSum !== 100) { setWarn(`Operator splits must sum to 100 (got ${splitSum})`); return; }
    setWarn('');
    const patch = { [opPctKey]: opPct, [jeffPctKey]: jeffPct };
    if (capexPctKey) patch[capexPctKey] = capexPct;
    patch.operator_splits = splits.map(s => ({ user_id: s.user_id, share_pct: Number(s.share_pct) }));
    onApply(patch);
  }

  return (
    <div className="rounded bg-cream-100/60 p-3 space-y-3 text-xs">
      <div className="grid grid-cols-3 gap-2">
        <PctInput label={`Operator (${tier})`} value={opPct} onChange={setOpPct} />
        <PctInput label={`Jeff (${tier})`}     value={jeffPct} onChange={setJeffPct} />
        {tier === 'T1' && <PctInput label="CapEx" value={capexPct} onChange={setCapexPct} />}
      </div>
      <div className={`text-xs ${tierSum !== 100 ? 'text-rust-700' : 'text-ink-700/60'}`}>
        Tier sum: {tierSum} / 100
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-ink-700/60 mb-1">Operator splits</div>
        {splits.map((o, i) => (
          <div key={o.user_id} className="flex items-center gap-2 mb-1">
            <div className="flex-1 text-ink-700">{o.name}</div>
            <PctInput
              label=""
              value={o.share_pct}
              onChange={v => setSplits(arr => arr.map((x, j) => j === i ? { ...x, share_pct: v } : x))}
              compact
            />
          </div>
        ))}
        <div className={`text-xs ${splitSum !== 100 ? 'text-rust-700' : 'text-ink-700/60'}`}>
          Split sum: {splitSum} / 100
        </div>
      </div>

      {warn && <div className="text-xs text-rust-700">{warn}</div>}

      <div className="flex justify-end">
        <button className="btn-primary text-xs py-1 px-3" onClick={applyAll}>Apply overrides</button>
      </div>
    </div>
  );
}

function PctInput({ label, value, onChange, compact }) {
  return (
    <div className={compact ? '' : ''}>
      {label && <label className="block text-xs text-ink-700/60">{label}</label>}
      <input
        type="number"
        min="0"
        max="100"
        className="input py-1 text-sm w-20"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
    </div>
  );
}
